/**
Basic modal with 3 slots for header, body and footer. Based on https://vuejs.org/v2/examples/modal.html
Some styles poached from bootstrap 4
Styles prefixed vue-modal- to prevent bootstrap clashes

example usage:
<modal v-if="showModal" v-on:close="showModal = false" size="small">
    <div slot="header">
        <h4>Title</h4>
    </div>

    <div slot="body">
        body
    </div>

    <div slot="footer">
        footer here
    </div>
</modal>
*/

<template>
    <transition name="modal">
        <div class="vue-modal-mask" v-on:click="close('background')" v-bind:class="verticalPositionClass">
            <div class="vue-modal-container" role="dialog" v-on:click.stop v-bind:class="sizeClass">
                <div class="vue-modal-wrapper" role="document">
                    <div class="vue-modal-header">
                        <slot name="header"></slot>

                        <slot name="header-close">
                            <button type="button" class="vue-modal-header-close" aria-label="Close" v-on:click="close('modal')"><span aria-hidden="true">&times;</span></button>
                        </slot>
                    </div>

                    <div class="vue-modal-body">
                        <slot name="body"></slot>
                    </div>

                    <div class="vue-modal-footer">
                        <slot name="footer"></slot>
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
    export default {
        props: {
            size: {type: String, default: "medium"},
            verticalPosition: {type: String, default: "middle"},
            clickBackgroundToClose: {type: Boolean, default: true},
        },
        data: function () {
            return {
                visibilityObserver: undefined
            }
        },
        methods: {
            close: function (source) {
                if (source === "background" && !this.clickBackgroundToClose) {
                    return;
                }

                document.body.classList.remove("no-scroll");
                this.$emit('close');
            },
            removeBodyScroll: function () {
                // duplicate functionality in close method, this is also triggered when the modal is closed
                // programmatically from outside the component
                document.body.classList.remove("no-scroll");

                this.visibilityObserver.disconnect();  // recreated in mount
            }
        },
        computed: {
            sizeClass: function () {
                switch (this.size) {
                    case "small":
                        return "vue-modal-sm";
                    case "large":
                        return "vue-modal-lg"
                }
            },
            verticalPositionClass: function () {
                switch (this.verticalPosition) {
                    case "top":
                        return "vue-modal-vert-top";
                    case "bottom":
                        return "vue-modal-vert-bottom";
                }

                return "";  // fall through to default
            }
        },
        mounted: function () {
            if ("MutationObserver" in window) {
                document.body.classList.add("no-scroll");

                this.visibilityObserver = new MutationObserver(this.removeBodyScroll);
                this.visibilityObserver.observe(this.$el, {childList: false, attributes: true})
            }


            this.$nextTick(function () {
                this.$emit("modal-displayed");
            });
        }
    }
</script>

<style lang="scss">
    @import "../../styles/_variables.scss";

    .vue-modal-mask {
        position: fixed;
        overflow-x: hidden; overflow-y: scroll;
        z-index: 9980;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, .5);
        padding: 5rem 0;
        transition: opacity .3s ease;
        display: flex;
        align-items: center;
    }

    .vue-modal-container {
        width: 300px;
        min-height: 100px;
        max-height: 100%;
        margin-left: auto; margin-right: auto;  // vertical dealt with by flex
        padding: 20px 30px;
        background-color: #fff;
        border-radius: 2px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, .33);
        transition: all .3s ease;
        overflow-y: scroll;
        flex: none !important;  // some parent elements use flex, stop inheriting here

        @media #{$breakpoint-sm} {
            width: 500px;
        }
    }

    .vue-modal-sm {
        width: 300px;
    }
    .vue-modal-lg {
        @media #{$breakpoint-sm} {
            width: 750px;
        }
    }

    .vue-modal-vert-top {
        align-items: flex-start;
    }

    .vue-modal-vert-bottom {
        align-items: flex-end;
    }

    .vue-modal-header {
        position: relative;
        min-height: 1rem;
    }

    .vue-modal-header-close {
        position: absolute;
        top: -10px; right: 0;
        font-weight: bold;
        line-height: 1;
        font-size: 200%;
        border: none;
        background: none;
        color: black;
        padding: 0;
        opacity: .5;

        &:not(:disabled):not(.disabled) {
            &:hover, &:focus {
                cursor: pointer;
                opacity: .75;
            }
        }
    }

    .vue-modal-body {
        margin: 15px 0;
    }

    /* Styles for VueJS transition (i.e. transition="modal") */
    .modal-enter {
        opacity: 0;
    }

    .modal-leave-active {
        opacity: 0;
    }

    .modal-enter .modal-container,
    .modal-leave-active .modal-container {
        -webkit-transform: scale(1.1);
        transform: scale(1.1);
    }

</style>